/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'transmute/get';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { CALYPSO_LIGHT, KOALA } from 'HubStyleTokens/colors';
import { WEB_FONT_MEDIUM_WEIGHT } from 'HubStyleTokens/misc';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIIcon from 'UIComponents/icon/UIIcon';
import PhoneFromNumberOptions from './PhoneFromNumberOptions';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getTwilioCallingProvider, getHubSpotCallingProvider } from 'calling-lifecycle-internal/call-provider/operators/callProviderOperators';
import { setPersistedConnectFromNumber, setPersistedFromNumber, setPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { PhoneNumberSources } from 'calling-lifecycle-internal/constants/PhoneNumberSources';
import { getSource } from 'calling-lifecycle-internal/operators/from-number/FromNumberGetters';
import { handleSetProviderSettings } from '../../callee-omnibus/utils/getOrCreateSettingsOmnibusStore';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CurrentSettingsWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__CurrentSettingsWrapper",
  componentId: "sc-1kpaxxn-0"
})(["border-style:solid;border-width:0 0 2px 0;border-color:", ";font-weight:", ";"], KOALA, WEB_FONT_MEDIUM_WEIGHT);
const CallSettingWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__CallSettingWrapper",
  componentId: "sc-1kpaxxn-1"
})(["cursor:pointer;background-color:transparent;&.is--disabled{opacity:0.5;}&:hover{background-color:", ";}"], CALYPSO_LIGHT);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__StyledUIIcon",
  componentId: "sc-1kpaxxn-2"
})(["float:right;"]);
const BodyWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfoActivityCreator__BodyWrapper",
  componentId: "sc-1kpaxxn-3"
})(["margin:-20px -24px 0;"]);
function CurrentSettingsInfoActivityCreator({
  asyncSettingsOmnibus,
  disabled,
  limits,
  onSelectFromNumber,
  selectedCallProvider,
  selectedFromNumber,
  scopes
}) {
  const providerName = useMemo(() => get('name', selectedCallProvider), [selectedCallProvider]);
  const [showPhoneFromNumberOptions, setShowPhoneFromNumberOptions] = useState(false);
  const handleShowPhoneFromNumberOptions = useCallback(() => {
    setShowPhoneFromNumberOptions(true);
  }, []);
  let numberOrProvider = providerName;
  if (selectedFromNumber) {
    if (selectedFromNumber.get('nickname') && selectedFromNumber.get('nickname').length) {
      numberOrProvider = `${selectedFromNumber.get('nickname')} ${selectedFromNumber.get('friendlyName')}`;
    } else if (selectedFromNumber.get('friendlyName')) {
      numberOrProvider = selectedFromNumber.get('friendlyName');
    }
  }
  const handleSelectFromNumber = useCallback(key => {
    const nextFromNumber = key.fromNumber;
    onSelectFromNumber(nextFromNumber);
    let callingProvider;
    if (getSource(nextFromNumber) === PhoneNumberSources.CONNECT_NUMBER) {
      setPersistedConnectFromNumber(nextFromNumber);
      callingProvider = getTwilioCallingProvider();
    } else {
      setPersistedFromNumber(nextFromNumber);
      callingProvider = getHubSpotCallingProvider();
    }
    setPersistedCallProvider(callingProvider);
    handleSetProviderSettings({
      callingProvider,
      fromNumber: nextFromNumber
    });
  }, [onSelectFromNumber]);
  return numberOrProvider ? /*#__PURE__*/_jsx(CurrentSettingsWrapper, {
    children: /*#__PURE__*/_jsx(UIPopover, {
      open: showPhoneFromNumberOptions,
      onOpenChange: () => setShowPhoneFromNumberOptions(false),
      animateOnToggle: true,
      width: "300px",
      content: {
        body: /*#__PURE__*/_jsx(UITooltip, {
          disabled: !disabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `callSettingsWidget.disableTooltip`
          }),
          children: /*#__PURE__*/_jsx(BodyWrapper, {
            children: /*#__PURE__*/_jsx(PhoneFromNumberOptions, {
              asyncSettingsOmnibus: asyncSettingsOmnibus,
              selectedProviderName: providerName,
              selectedFromNumber: selectedFromNumber,
              hasCallingProvidersCapability: false,
              onSelect: handleSelectFromNumber,
              disabled: disabled,
              scopes: scopes,
              limits: limits
            })
          })
        })
      },
      placement: "right bottom",
      arrowSize: "none",
      children: /*#__PURE__*/_jsxs(CallSettingWrapper, {
        className: "p-x-4 p-y-3",
        onClick: handleShowPhoneFromNumberOptions,
        "data-selenium-test": "open-pre-call-popup-from-numbers",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "callSettingsWidget.callFrom",
          options: {
            numberOrProvider
          }
        }), /*#__PURE__*/_jsx(StyledUIIcon, {
          name: "right",
          size: "xxs"
        })]
      })
    })
  }) : null;
}
CurrentSettingsInfoActivityCreator.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedCallProvider: RecordPropType('CallingProvider'),
  selectedFromNumber: RecordPropType('FromNumber'),
  onSelectFromNumber: PropTypes.func.isRequired,
  scopes: ImmutablePropTypes.map.isRequired,
  limits: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool
};
export default /*#__PURE__*/memo(CurrentSettingsInfoActivityCreator);